
import { useState, useEffect } from 'react';

export interface Customer {
  id: number;
  name: string;
  type: '供应商' | '客户';
  contact: string;
  phone: string;
  address: string;
  totalAmount: number;
  lastDate: string;
  creditCode?: string;
  idCard?: string;
  remark?: string;
}

const STORAGE_KEY = 'grain_customers';

const defaultCustomers: Customer[] = [
  {
    id: 1,
    name: '张三农场',
    type: '供应商',
    contact: '张三',
    phone: '13800138001',
    address: '河南省周口市',
    totalAmount: 4200000,
    lastDate: '2026-02-15',
    creditCode: '',
    idCard: '410000199001011234',
    remark: '长期合作供应商，信誉良好',
  },
  {
    id: 2,
    name: '李四粮油公司',
    type: '客户',
    contact: '李四',
    phone: '13800138002',
    address: '河南省郑州市',
    totalAmount: 3850000,
    lastDate: '2026-02-15',
    creditCode: '91410100MA45XXXX1X',
    idCard: '',
    remark: '主要销售客户，月结账期30天',
  },
  {
    id: 3,
    name: '王五合作社',
    type: '供应商',
    contact: '王五',
    phone: '13800138003',
    address: '河南省驻马店市',
    totalAmount: 2940000,
    lastDate: '2026-02-14',
    creditCode: '91411700MA45XXXX2X',
    idCard: '',
    remark: '',
  },
  {
    id: 4,
    name: '赵六面粉厂',
    type: '客户',
    contact: '赵六',
    phone: '13800138004',
    address: '河南省商丘市',
    totalAmount: 5670000,
    lastDate: '2026-02-14',
    creditCode: '91411400MA45XXXX3X',
    idCard: '',
    remark: '大客户，优先保障供货',
  },
  {
    id: 5,
    name: '孙七农业',
    type: '供应商',
    contact: '孙七',
    phone: '13800138005',
    address: '河南省南阳市',
    totalAmount: 1890000,
    lastDate: '2026-02-13',
    creditCode: '',
    idCard: '411300199205156789',
    remark: '',
  },
  {
    id: 6,
    name: '周八饲料厂',
    type: '客户',
    contact: '周八',
    phone: '13800138006',
    address: '河南省洛阳市',
    totalAmount: 4320000,
    lastDate: '2026-02-13',
    creditCode: '91410300MA45XXXX4X',
    idCard: '',
    remark: '饲料用粮，需提供质检报告',
  },
];

function loadCustomers(): Customer[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Customer[];
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch (e) {
    console.error('Failed to load customers from localStorage:', e);
  }
  return defaultCustomers;
}

function saveCustomers(customers: Customer[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(customers));
  } catch (e) {
    console.error('Failed to save customers to localStorage:', e);
  }
}

// Global in‑module state (singleton shared across components)
let globalCustomers: Customer[] = loadCustomers();
const listeners: Set<() => void> = new Set();

function notifyListeners() {
  listeners.forEach((fn) => fn());
}

export function getCustomers(): Customer[] {
  return globalCustomers;
}

export function getSuppliers(): Customer[] {
  return globalCustomers.filter((c) => c.type === '供应商');
}

export function getBuyers(): Customer[] {
  return globalCustomers.filter((c) => c.type === '客户');
}

export function addCustomer(customer: Omit<Customer, 'id'>): Customer {
  const newId =
    globalCustomers.length > 0
      ? Math.max(...globalCustomers.map((c) => c.id)) + 1
      : 1;
  const newCustomer: Customer = { ...customer, id: newId };
  globalCustomers = [...globalCustomers, newCustomer];
  saveCustomers(globalCustomers);
  notifyListeners();
  return newCustomer;
}

export function updateCustomer(updated: Customer): void {
  globalCustomers = globalCustomers.map((c) =>
    c.id === updated.id ? { ...updated } : c,
  );
  saveCustomers(globalCustomers);
  notifyListeners();
}

export function deleteCustomer(id: number): void {
  globalCustomers = globalCustomers.filter((c) => c.id !== id);
  saveCustomers(globalCustomers);
  notifyListeners();
}

export function useCustomers() {
  const [customers, setCustomers] = useState<Customer[]>(() => globalCustomers);

  useEffect(() => {
    const refresh = () => setCustomers([...globalCustomers]);
    listeners.add(refresh);
    return () => {
      listeners.delete(refresh);
    };
  }, []);

  return {
    customers,
    suppliers: customers.filter((c) => c.type === '供应商'),
    buyers: customers.filter((c) => c.type === '客户'),
    addCustomer,
    updateCustomer,
    deleteCustomer,
  };
}
